import tapWordCode from './samples/tap-word.js?raw';
import { api } from '../api/client';

export type StudioField =
  | { id: string; label: string; type: 'words'; min?: number; max?: number; hint?: string }
  | { id: string; label: string; type: 'number'; min: number; max: number; fallback: number; hint?: string }
  | { id: string; label: string; type: 'choice'; options: { value: string; label: string }[]; fallback: string; hint?: string }
  | { id: string; label: string; type: 'text'; max?: number; fallback?: string; hint?: string };

export type StudioManifest = {
  id: string;
  version: number;
  name: string;
  describes: string;
  content: StudioField[];
  params: StudioField[];
};

export type StudioType = { manifest: StudioManifest; code: string };

export const SAMPLE_TYPES: StudioType[] = [
  {
    manifest: {
      id: 'sample-tap-word',
      version: 1,
      name: 'Tap the word (Studio sample)',
      describes: 'The learner hears a word and taps the tile that shows it. Built through Studio to prove the game bridge.',
      content: [{ id: 'words', label: 'Words to practise', type: 'words', min: 2, max: 40 }],
      params: [{ id: 'choices', label: 'Tiles on screen', type: 'number', min: 2, max: 4, fallback: 3 }],
    },
    code: tapWordCode,
  },
];

type ServerType = { id: string; name: string; manifest: StudioManifest; code: string };

export async function listStudioTypes(): Promise<StudioType[]> {
  const rows = await api<ServerType[]>('/studio/types', { token: true });
  return rows.map((r) => ({ manifest: r.manifest, code: r.code }));
}

export async function loadStudioType(id: string): Promise<StudioType | null> {
  const sample = SAMPLE_TYPES.find((t) => t.manifest.id === id);
  if (sample) return sample;
  try {
    const r = await api<ServerType>(`/studio/types/${encodeURIComponent(id)}`, { token: true });
    return { manifest: r.manifest, code: r.code };
  } catch {
    return null;
  }
}

export function studioConfig(manifest: StudioManifest, config: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of [...manifest.content, ...manifest.params]) {
    const v = config[f.id];
    if (f.type === 'words') out[f.id] = Array.isArray(v) ? v.map(String).filter(Boolean).slice(0, f.max ?? 200) : [];
    else if (f.type === 'number') out[f.id] = typeof v === 'number' && v >= f.min && v <= f.max ? v : f.fallback;
    else if (f.type === 'choice') out[f.id] = typeof v === 'string' && f.options.some((o) => o.value === v) ? v : f.fallback;
    else out[f.id] = typeof v === 'string' ? v.slice(0, f.max ?? 500) : f.fallback ?? '';
  }
  return out;
}
